import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useSelector, useDispatch, shallowEqual } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Autocomplete } from '@totalsoft/rocket-ui';
import { computeFacilityNextStatesList, updateFacilityState } from '../actionCreators';
import { validateStateChange } from 'features/document/edit/documents/actionCreators';
import { getFacilityHeader } from '../../../wizard/selectors';
import { getFacilityNextStates } from '../selectors';
import { Grid } from '@mui/material';

const FacilityStatesSelectionListContainer = ({ documentId, siteId }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const { loaded: facilityHeaderLoaded, content: facilityHeader } = useSelector(
    state => getFacilityHeader(state, documentId),
    shallowEqual
  );

  const { loaded: nextStatesLoaded, content: nextStates } = useSelector(state => getFacilityNextStates(documentId, state), shallowEqual);

  const [selectedState, setSelectedState] = useState(null);
  const [maxLength, setMaxLength] = useState(0);

  useEffect(() => {
    if (nextStatesLoaded) setMaxLength(nextStates.reduce((max, elem) => Math.max(max, elem.stateName.length), 0));
  }, [nextStatesLoaded, nextStates]);

  useEffect(() => {
    if (facilityHeaderLoaded && selectedState === null)
      setSelectedState({ stateId: facilityHeader.documentStateId, stateName: facilityHeader.documentStateName });
  }, [facilityHeader, facilityHeaderLoaded, selectedState]);

  useEffect(() => {
    if (!nextStatesLoaded) dispatch(computeFacilityNextStatesList(documentId, siteId));
  }, [documentId, siteId, nextStatesLoaded, dispatch]);

  const onStateChangeValidatedCallback = nextSelectedState => {
    if (
      nextSelectedState.stateId === selectedState.stateId ||
      (nextSelectedState.requiresConfirmation && !window.confirm(t('ConfirmStateChange')))
    )
      return;
    dispatch(updateFacilityState(documentId, siteId, nextSelectedState.stateId, () => setSelectedState(nextSelectedState)));
  };

  const handleSelectState = nextSelectedState => {
    dispatch(
      validateStateChange(documentId, siteId, selectedState.stateId, nextSelectedState.stateId, () =>
        onStateChangeValidatedCallback(nextSelectedState)
      )
    );
  };

  return (
    <Grid item xs='auto'>
      <Autocomplete
        valueKey='stateId'
        labelKey='stateName'
        value={{ stateId: facilityHeader.documentStateId, stateName: facilityHeader.documentStateName }}
        onChange={handleSelectState}
        sx={{ minWidth: `${maxLength * 8}px` }}
        isSearchable={false}
        isClearable={false}
        options={nextStates}
      />
    </Grid>
  );
};

FacilityStatesSelectionListContainer.propTypes = {
  documentId: PropTypes.number.isRequired,
  siteId: PropTypes.number.isRequired
};

export default FacilityStatesSelectionListContainer;
